import React, { useState, useMemo } from "react";
import AppDataGrid from "../../common/AppDataGrid";
import {
  CircularProgress,
  Box,
  Container,
  Snackbar,
  Alert,
  Button,
} from "@mui/material";
import useFirestore from "../../../config/fireBaseHook";
import Add from "./inc/Add";
import Edit from "./inc/Edit";
import Delete from "./inc/Delete";
import EditUpload from "../../../hooks/EditUpload";
import ClearCollectionButton from "../../common/ClearCollectionButton";
import TechPrepPortalMgtBreadCrumbs from "../../Layout/TechPrepPortalMgtBreadCrumbs";
import { useAuth } from "../../../config/AuthContext";
import SimpleToolbar from "../../common/SimpleToolbar";
import CustomFilterPanel from "../../common/CustomFilterPanel";
import useCustomFilter from "../../../hooks/useCustomFilter";
import { elearnShellTypeFilters } from "../filterConfigurations";

const ElearnShellType = () => {
  const { userPermissions } = useAuth();
  const canEdit = userPermissions["Elearn Shell Type"]?.edit;
  const canDelete = userPermissions["Elearn Shell Type"]?.delete;
  const { data: shellTypes, loading, error } = useFirestore("ElearnShellType");
  const [selectedShellType, setSelectedShellType] = useState(null);
  const [editDialogOpen, setEditDialogOpen] = useState(false);
  const [deleteDialogOpen, setDeleteDialogOpen] = useState(false);
  const [snackbarOpen, setSnackbarOpen] = useState(false);
  const [snackbarMessage, setSnackbarMessage] = useState("");
  const [snackbarSeverity, setSnackbarSeverity] = useState("success");
  const [sortModel, setSortModel] = useState([]);
  const [paginationModel, setPaginationModel] = useState({
    pageSize: 100,
    page: 0,
  });
  const pageName = "Elearn Shell Type";

  // Custom filter implementation
  const {
    filteredData,
    filterModel,
    setFilterModel,
    resetFilters,
    hasActiveFilters,
    isFiltering,
  } = useCustomFilter(shellTypes || [], elearnShellTypeFilters);

  // Handle filter changes
  const handleFiltersChange = (newFilters) => {
    setFilterModel(newFilters);
    setPaginationModel((prev) => ({ ...prev, page: 0 }));
  };

  const columns = useMemo(
    () => [
      {
        field: "name",
        headerName: "Name",
        flex: 1,
        minWidth: 200,
      },
      ...(canDelete
        ? [
            {
              field: "actions",
              headerName: "Actions",
              flex: 0.5,
              minWidth: 100,
              sortable: false,
              filterable: false,
              renderCell: (params) => (
                <Button
                  variant="outlined"
                  color="error"
                  size="small"
                  onClick={(e) => {
                    e.stopPropagation(); // Prevent row click
                    setSelectedShellType(params.row);
                    setDeleteDialogOpen(true);
                  }}
                  sx={{ fontSize: "0.75rem", padding: "2px 8px" }}
                >
                  Delete
                </Button>
              ),
            },
          ]
        : []),
    ],
    [canDelete],
  );

  // Prepare data for DataGrid
  const rows = useMemo(() => {
    if (!filteredData || !Array.isArray(filteredData)) return [];
    return filteredData.map((shellType, index) => ({
      id: shellType.id || index,
      ...shellType,
    }));
  }, [filteredData]);

  const handleEditDialogClose = () => {
    setEditDialogOpen(false);
    setSelectedShellType(null);
  };

  const handleDeleteDialogClose = () => {
    setDeleteDialogOpen(false);
    setSelectedShellType(null);
  };

  const handleSnackbarClose = (event, reason) => {
    if (reason === "clickaway") return;
    setSnackbarOpen(false);
  };

  const handleRecordUpdated = () => {
    setSnackbarMessage("Record has been updated successfully!");
    setSnackbarSeverity("success");
    setSnackbarOpen(true);
    setEditDialogOpen(false);
  };

  const handleRecordAdded = () => {
    setSnackbarMessage("Record has been added successfully!");
    setSnackbarSeverity("success");
    setSnackbarOpen(true);
  };

  const handleRecordDeleted = () => {
    setSnackbarMessage("Record has been deleted successfully!");
    setSnackbarSeverity("success");
    setSnackbarOpen(true);
    setDeleteDialogOpen(false);
  };

  const handleBulkActionComplete = (success, message) => {
    setSnackbarMessage(
      message ||
        (success ? "Bulk operation successful." : "Bulk operation failed."),
    );
    setSnackbarSeverity(success ? "success" : "error");
    setSnackbarOpen(true);
  };

  const handleRowClick = (params) => {
    if (canEdit) {
      setSelectedShellType(params.row);
      setEditDialogOpen(true);
    }
  };

  const exportColumnOrder = columns.map((col) => col.field);
  const templateFields = exportColumnOrder.filter(
    (field) => field !== "actions",
  );

  if (loading) {
    return (
      <Container maxWidth="xl" sx={{ mt: 15 }}>
        <Box
          display="flex"
          justifyContent="center"
          alignItems="center"
          height="50vh"
        >
          <CircularProgress />
        </Box>
      </Container>
    );
  }

  if (error) {
    return (
      <Container maxWidth="xl" sx={{ mt: 15 }}>
        <Box
          display="flex"
          justifyContent="center"
          alignItems="center"
          height="50vh"
        >
          <Alert severity="error">Error loading data: {error.message}</Alert>
        </Box>
      </Container>
    );
  }

  return (
    <Container maxWidth="xl" sx={{ mt: 15 }}>
      <TechPrepPortalMgtBreadCrumbs pageName={pageName} />

      {/* Custom Filter Panel */}
      <CustomFilterPanel
        filterConfig={elearnShellTypeFilters}
        onFiltersChange={handleFiltersChange}
        initialFilters={filterModel}
        collapsible={true}
        title="Elearn Shell Type Filters"
        resultCount={filteredData?.length || 0}
        totalCount={shellTypes?.length || 0}
        isSearching={isFiltering}
      />

      {/* Add button - fallback if toolbar doesn't show it */}
      {canEdit && (
        <Box sx={{ mb: 2, display: "flex", gap: 1, alignItems: "center" }}>
          <Add onRecordAdded={handleRecordAdded} />
          <ClearCollectionButton collectionName="ElearnShellType" />
        </Box>
      )}

      <Box sx={{ height: "85vh", width: "100%", mt: 2 }}>
        <AppDataGrid
          rows={rows}
          columns={columns}
          loading={loading}
          sortModel={sortModel}
          onSortModelChange={setSortModel}
          paginationModel={paginationModel}
          onPaginationModelChange={setPaginationModel}
          pageSizeOptions={[25, 50, 100, 250, 500]}
          onRowClick={handleRowClick}
          slots={{
            toolbar: () => (
              <SimpleToolbar
                exportColumnOrder={exportColumnOrder}
                rows={rows}
                onBulkActionComplete={handleBulkActionComplete}
                pageName={pageName}
                addComponent={
                  canEdit ? <Add onRecordAdded={handleRecordAdded} /> : null
                }
                editUploadComponent={
                  canEdit ? (
                    <EditUpload
                      collectionName="ElearnShellType"
                      onBulkActionComplete={handleBulkActionComplete}
                      templateFields={templateFields}
                    />
                  ) : null
                }
              />
            ),
          }}
          sx={{
            "& .MuiDataGrid-row:hover": {
              cursor: canEdit ? "pointer" : "default",
            },
          }}
        />
      </Box>

      {selectedShellType && (
        <Edit
          open={editDialogOpen}
          onClose={handleEditDialogClose}
          shellType={selectedShellType}
          onRecordUpdated={handleRecordUpdated}
        />
      )}

      {selectedShellType && (
        <Delete
          open={deleteDialogOpen}
          onClose={handleDeleteDialogClose}
          shellType={selectedShellType}
          onRecordDeleted={handleRecordDeleted}
        />
      )}

      <Snackbar
        open={snackbarOpen}
        autoHideDuration={6000}
        onClose={handleSnackbarClose}
        anchorOrigin={{ vertical: "top", horizontal: "center" }}
      >
        <Alert
          onClose={handleSnackbarClose}
          severity={snackbarSeverity}
          sx={{ width: "100%" }}
        >
          {snackbarMessage}
        </Alert>
      </Snackbar>
    </Container>
  );
};

export default ElearnShellType;
